import React, { useCallback, useEffect, useState } from "react";
import { Box, Paper, Tabs, Tab, Typography } from "@material-ui/core";
import { SpecUploader } from "../../components/SpecUploader";
import { SpecDetails } from "../../components/SpecDetails";
import ApiService from "../../services/ApiService";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import { ResourceAuthorizer } from "../../../authorizer/components/Authorizer";
import ApiReference from "../../../consumer/components/products/product/apireference/APIReference";
import LintingMetrics from "../../../consumer/components/products/product/apireference/LintingMetrics";
import QualitativeMetrics from "../../../consumer/components/products/product/apireference/QualitativeMetrics";

interface ApiSpecificationTabProps {
  serviceId: string;
  consumerKey: string;
  applicationName: string;
  isLintingEnabled: boolean;
  version: string;
}

const ApiSpecificationTab: React.FC<ApiSpecificationTabProps> = ({
  serviceId,
  consumerKey,
  applicationName,
  isLintingEnabled,
  version
}) => {
  const [specFile, setSpecFile] = useState<string>();
  const [specId, setSpecId] = useState<string>("");
  const [nestedTabValue, setNestedTabValue] = useState(0);
  const [specUrl, setSpecUrl] = useState<string>("");
  const [lintingResultsUri, setLintingResultsUri] = useState<string>("");
  const [qualitativeResultsUri, setQualitativeResultsUri] = useState<string>("");
  const [lintingStatus, setLintingStatus] = useState<string>("");
  const [apiStandardVersion, setApiStandardVersion] = useState<string>("");
  const [documentationUrl, setDocumentationUrl] = useState<string>("");

  // Version deployment hook
  const [state, { subscribe }] = useAsyncAction(() =>
    ApiService.getVersionSpec(consumerKey, version)
  );

  // Fetch version deployment data on component mount
  useEffect(() => {
    if (consumerKey && version) {
      subscribe();
    }
  }, [consumerKey, version, subscribe]);

  // Update spec details when version deployment data changes
  useEffect(() => {
    if (!state?.loaded) {
      return;
    }
    
    const responseData = state?.value as unknown as {
      [key: string]: any;
    };
      setSpecFile(responseData?.specFile);
      setSpecId(responseData?.specId);
      setSpecUrl(responseData?.specUrl);
      setLintingResultsUri(responseData?.lintingResultsUri);
      setQualitativeResultsUri(responseData?.qualitativeResultsUri);
      setLintingStatus(responseData?.lintingStatus);
        setApiStandardVersion(responseData?.apiStandardVersion || "");
        setDocumentationUrl(responseData?.documentationUrl || "");
  }, [state]);

  return (
    <Box>
      {/* Spec Management Section */}
      <>
        {!specFile && state?.loaded && (
          <Box style={{
            paddingTop: "26px",
            paddingLeft: "32px",
            paddingBottom: "20px"
          }}>
            <ResourceAuthorizer action={"update"} resourceType={"SERVICE"} resourceId={serviceId || ""} hideErrorMessage>
              <SpecUploader
                apiId={consumerKey}
                version={version}
                refreshDeployments={subscribe}
              />
            </ResourceAuthorizer>
          </Box>
        )}
        {state?.loaded && specFile && (
          <SpecDetails
            serviceId={serviceId}
            specName={specFile}
            version={version}
            apiId={consumerKey}
            specId={specId}
            specFile={specFile}
            refreshDeployments={subscribe}
          />
        )}
      </>
      
      {/* Nested tabs for API SPECIFICATION - Always render the tabs */}
      <>
      {specFile && (
        <Box paddingTop="16px">
          <Tabs
            value={nestedTabValue}
            onChange={(event, newValue) => setNestedTabValue(newValue)}
            indicatorColor="primary"
            textColor="primary"
            style={{ 
              marginBottom: 24,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              backgroundColor: "#f3f3f7",
              padding: "0 16px"
            }}
          >
            <Tab label="API Reference" value={0} style={{ fontWeight: 600, padding: "12px 20px" }} />
            {isLintingEnabled && <Tab label="Linting Metrics" value={1} style={{ fontWeight: 600, padding: "12px 20px" }} />}
            {isLintingEnabled && <Tab label="Qualitative Metrics" value={2} style={{ fontWeight: 600, padding: "12px 20px" }} />}
          </Tabs>
          
          {/* API Reference Tab Content */}
          {nestedTabValue === 0 && (
            <Box>
              {state?.loaded && specUrl && (
                <ApiReference
                  specDetails={{
                    prod: { fileName: specUrl || '' },
                    preProd: { fileName: specUrl || '' }
                  }}
                  redocServerUrl=" "
                  specUrl={process.env.REACT_APP_SPEC_URL as string}
                  productName={applicationName}
                />
              )}
            </Box>
          )}
          
          {/* Linting Metrics Tab */}
          {isLintingEnabled && nestedTabValue === 1 && (
            <Box>
              {state?.loaded && lintingResultsUri && (
                <LintingMetrics
                  specLintResultUrl={lintingResultsUri || ''}
                  specFile={specFile}
                  lintingStatus={lintingStatus || ''}
                  apiStandardVersion={apiStandardVersion || ''}
                  documentationUrl={documentationUrl || ''}
                />
              )}
            </Box>
          )}
          
          {/* Qualitative Metrics Tab */}
          {isLintingEnabled && nestedTabValue === 2 && (
            <Box>
              {state?.loaded && qualitativeResultsUri && (
                <QualitativeMetrics
                  specQualitativeResultsUrl={qualitativeResultsUri || ''}
                  specFile={specFile}
                  lintingStatus={lintingStatus || ''}
                />
              )}
            </Box>
          )}
        </Box>)
        }
      </>
    </Box>
  );
};

export default ApiSpecificationTab;